/**
 * Campaign (UTM) parameter capture.
 *
 * Values arrive on the landing URL (e.g. /Enterprise?utm_source=LinkedIn...)
 * and must survive later navigation / query-string rewrites inside the flow,
 * so they are persisted in sessionStorage for the rest of the visit.
 */

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_KEY = 'pd_utm';

function readFromUrl(): UtmParams {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const found: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) found[key] = value.slice(0, 255);
  }
  return found;
}

/**
 * Capture campaign params from the current URL. First campaign link wins:
 * stored values are only replaced when a new URL actually carries UTMs.
 */
export function captureUtmParams(): UtmParams {
  const fromUrl = readFromUrl();
  if (Object.keys(fromUrl).length === 0) return getUtmParams();

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
  } catch (e) {
    console.warn('[utm] Failed to persist campaign params:', e);
  }
  return fromUrl;
}

/** Stored campaign params, falling back to the current URL. */
export function getUtmParams(): UtmParams {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as UtmParams;
      const clean: UtmParams = {};
      for (const key of UTM_KEYS) {
        const value = parsed?.[key];
        if (typeof value === 'string' && value.trim()) clean[key] = value.trim();
      }
      if (Object.keys(clean).length > 0) return clean;
    }
  } catch (e) {
    console.warn('[utm] Failed to read campaign params:', e);
  }
  return readFromUrl();
}
